import React, { useEffect, useState } from "react";
import { Mail, Loader2, CheckCircle2, History, MousePointerClick, Undo2, AlarmClock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Feature = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3">
    <Icon size={18} className="mt-0.5 shrink-0" style={{ color: "var(--brand-primary)" }} />
    <div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{children}</p>
    </div>
  </div>
);

export default function AdminNotifications() {
  const [state, setState] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overdueBusy, setOverdueBusy] = useState(false);

  const load = () => api.get("/digest/preview").then(({ data }) => setState(data));
  const loadOverdue = () => api.get("/overdue/preview").then(({ data }) => setOverdue(data));
  useEffect(() => { load(); loadOverdue(); }, []);

  const toggleOverdue = async () => {
    setOverdueBusy(true);
    try {
      const { data } = await api.put("/overdue/settings", { enabled: !overdue.enabled });
      setOverdue((s) => ({ ...s, enabled: data.enabled }));
      toast.success(data.enabled ? "Overdue reminders turned on" : "Overdue reminders turned off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setOverdueBusy(false);
    }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/digest/settings", { enabled: !state.enabled });
      setState((s) => ({ ...s, enabled: data.enabled }));
      toast.success(data.enabled ? "Friday digest turned on" : "Friday digest turned off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      testId="admin-notifications-page"
      title="Notifications"
      subtitle="The weekly delivery digest, and how change tracking works across your workspace"
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="panel p-6 lg:col-span-2" data-testid="digest-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-800">Friday delivery digest</h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                Every Friday at 4pm, each administrator, PMO and project manager receives a short email
                listing what changed on their projects that week — busiest project first, with who made
                the changes. Project managers see the projects they are named on; administrators and PMO
                see the whole workspace. A quiet week sends nothing at all.
              </p>
            </div>
            <Button
              data-testid="digest-toggle-btn"
              onClick={toggle}
              disabled={busy || !state}
              className="shrink-0 gap-1.5"
              style={{ backgroundColor: state?.enabled ? "#64748B" : "var(--brand-primary)" }}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {state?.enabled ? "Turn off" : "Turn on"}
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
            <span
              data-testid="digest-status"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${
                state?.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <CheckCircle2 size={12} /> {state?.enabled ? "On — sending each Friday" : "Off"}
            </span>
            {state?.last_sent_at && (
              <span className="text-slate-500">Last sent {state.last_sent_at.slice(0, 10)}</span>
            )}
          </div>

          <div className="mt-6 border-t border-slate-100 pt-5">
            <p className="field-label">Your preview — the last {state?.window_days || 7} days</p>
            {!state && <p className="mt-2 text-sm text-slate-500">Loading…</p>}
            {state?.projects?.length === 0 && (
              <p className="mt-2 text-sm text-slate-500">
                Nothing has changed on your projects this week, so you would receive no email.
              </p>
            )}
            {state?.projects?.length > 0 && (
              <>
                <p className="mt-1 text-sm text-slate-600">
                  {state.total_changes} change{state.total_changes === 1 ? "" : "s"} across{" "}
                  {state.projects.length} project{state.projects.length === 1 ? "" : "s"}.
                </p>
                <ul className="mt-3 divide-y divide-slate-100" data-testid="digest-preview-list">
                  {state.projects.map((p) => (
                    <li key={p.project_id} className="py-2.5">
                      <p className="text-sm font-medium text-slate-800">{p.project}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {p.changes} change{p.changes === 1 ? "" : "s"}
                        {p.breakdown?.length > 0 && ` — ${p.breakdown.map(([l, c]) => `${l} (${c})`).join(", ")}`}
                        {p.editors?.length > 0 && ` · by ${p.editors.join(", ")}`}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="panel p-6 lg:col-span-2" data-testid="overdue-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-800">Overdue control reminders</h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                Each weekday morning, anyone who owns a risk, issue, assumption, dependency, change
                request or milestone that has gone past its date gets an email listing{" "}
                <strong>only their own</strong> overdue items, worst first. Anything whose owner does
                not match a user in the workspace goes to administrators and PMO instead, so nothing
                falls in a gap. Each item is chased once, then only again if it is still overdue{" "}
                {overdue?.rechase_days || 7} days later.
              </p>
            </div>
            <Button
              data-testid="overdue-toggle-btn"
              onClick={toggleOverdue}
              disabled={overdueBusy || !overdue}
              className="shrink-0 gap-1.5"
              style={{ backgroundColor: overdue?.enabled ? "#64748B" : "var(--brand-primary)" }}
            >
              {overdueBusy ? <Loader2 size={14} className="animate-spin" /> : <AlarmClock size={14} />}
              {overdue?.enabled ? "Turn off" : "Turn on"}
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
            <span
              data-testid="overdue-status"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${
                overdue?.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <CheckCircle2 size={12} /> {overdue?.enabled ? "On — chasing owners each weekday" : "Off"}
            </span>
            {overdue && (
              <span className="text-slate-500" data-testid="overdue-totals">
                {overdue.total_items} overdue item{overdue.total_items === 1 ? "" : "s"} across{" "}
                {overdue.people} {overdue.people === 1 ? "person" : "people"} right now
              </span>
            )}
          </div>

          <div className="mt-6 border-t border-slate-100 pt-5">
            <p className="field-label">Yours right now</p>
            {!overdue && <p className="mt-2 text-sm text-slate-500">Loading…</p>}
            {overdue?.mine?.length === 0 && (
              <p className="mt-2 text-sm text-slate-500">
                Nothing of yours is overdue, so you would receive no email.
              </p>
            )}
            {overdue?.mine?.length > 0 && (
              <ul className="mt-3 divide-y divide-slate-100" data-testid="overdue-preview-list">
                {overdue.mine.map((i) => (
                  <li key={`${i.collection}-${i.id}`} data-testid={`overdue-item-${i.id}`} className="flex items-baseline justify-between gap-4 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{i.title}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {i.ref ? `${i.ref} · ` : ""}{i.kind} · {i.project}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-semibold text-rose-700">
                      {i.days_overdue} day{i.days_overdue === 1 ? "" : "s"} overdue
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="panel p-6" data-testid="change-tracking-card">
          <h3 className="font-display text-base font-semibold text-slate-800">What your team can now do</h3>
          <div className="mt-4 space-y-5">
            <Feature icon={History} title="Change history on every record">
              Open any risk, issue, assumption, decision, change, lesson, benefit, milestone or the
              project record itself and expand <strong>Change history</strong> to see who changed what,
              when, and what it said before.
            </Feature>
            <Feature icon={Undo2} title="One-click undo">
              Any entry in that history has an <strong>Undo</strong> button that puts those values back.
              The revert is itself recorded, so the audit trail stays complete.
            </Feature>
            <Feature icon={MousePointerClick} title="Edit straight in the register">
              On the cross-project registers, click any highlighted cell to change it in place. Enter
              saves, Escape cancels — no dialog needed.
            </Feature>
          </div>
        </div>
      </div>
    </Shell>
  );
}
